import { Fragment, type ReactNode } from "react";

/**
 * The little Markdown the brand's legal texts use (`legal.privacy`, `terms`,
 * `attribution`): `#`/`##`/`###` headings, paragraphs, `- ` lists, `**bold**`
 * and `[links](url)`. Rendered as React elements, never as HTML; links only
 * to http(s), mailto or same-site paths.
 */
export function Markdown({ text }: { text: string }) {
  return <>{blocks(text)}</>;
}

/** One line of inline Markdown (bold, links), e.g. the footer attribution. */
export function InlineMarkdown({ text }: { text: string }) {
  return <>{inline(text)}</>;
}

function blocks(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const chunks = text.replace(/\r\n/g, "\n").trim().split(/\n\s*\n/);
  chunks.forEach((chunk, i) => {
    const lines = chunk.split("\n").map((l) => l.trim());
    const heading = /^(#{1,3})\s+(.*)$/.exec(lines[0]);
    if (heading && lines.length === 1) {
      const Tag = (["h1", "h2", "h3"] as const)[heading[1].length - 1];
      out.push(<Tag key={i}>{inline(heading[2])}</Tag>);
    } else if (lines.every((l) => l.startsWith("- "))) {
      out.push(
        <ul key={i}>
          {lines.map((l, j) => (
            <li key={j}>{inline(l.slice(2))}</li>
          ))}
        </ul>,
      );
    } else {
      out.push(<p key={i}>{inline(lines.join(" "))}</p>);
    }
  });
  return out;
}

const SAFE_URL = /^(https?:\/\/|mailto:|\/(?!\/))/i;

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={m.index}>{m[1]}</strong>);
    else if (SAFE_URL.test(m[3])) {
      const external = /^https?:/i.test(m[3]);
      out.push(
        <a key={m.index} href={m[3]} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
          {m[2]}
        </a>,
      );
    } else out.push(<Fragment key={m.index}>{m[2]}</Fragment>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
